// Forked from ../../SearchByImage/SearchByImageModal.tsx — keep in sync until phase_0 cleanup.
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { IconArrowLeft, IconImage } from '@bynder/icons';
import { Button, Flex, Input, notify, textStyles, token } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { HELP_FOOTER_HEIGHT, HelpFooter } from '../../Help/HelpFooter';
import { useShowHelpFooter } from '../../Help/useShowHelpFooter';
import FileSelector from './FileSelector';
import { isValidUrl } from '../../../../helpers/url';
import { SimilarityFieldName, type SimilarityFilter } from '../../helpers/types';
import { isReactAssetOverviewActive } from '../../helpers/searchUtils';
import { emitSimilaritySearchEvent } from '../../NLSSearch/utils';
import { fileToDataUrl, resizeImage } from '../../helpers/searchByImage';
import { storeSearchByImageData } from '../../storage/SearchByImageUploadService';
import { useNavigableItem } from '../../KeyboardNavigation';
import { useSearchMode } from '../../SpotlightState';

type Props = {
	onClickReturn: () => void;
	onSubmit: () => void;
};

/** Context for AI: inline search-by-image (upload a file or paste an image URL) → a similarity search */
export function SearchByImagePanel({ onClickReturn, onSubmit }: Props) {
	const { translate } = useTranslate();
	const { isHybridSearchOn } = useSearchMode();
	const showHelpFooter = useShowHelpFooter();

	const [imageURL, setImageURL] = useState('');
	const [isSearchingByLink, setIsSearchingByLink] = useState(false);
	const [isUploadingImage, setIsUploadingImage] = useState(false);
	const isInvalidInput = !isValidUrl(imageURL);

	const { isActive: isReturnActive, itemRef: returnButtonRef } =
		useNavigableItem<HTMLButtonElement>(onClickReturn, { afterSelect: 'none' });
	const focusAnchorRef = useRef<HTMLSpanElement>(null);

	// This panel replaces the search bar and its input, so once it opens there's no
	// longer a focused element on the page, and arrow keys/Enter stop working until
	// something is focused again. Focus this anchor rather than the Button itself,
	// which would show a visible focus ring.
	useEffect(() => {
		focusAnchorRef.current?.focus();
	}, []);

	const handleUpload = async (file: File[]) => {
		setIsUploadingImage(true);

		const selectedFile = file[0];

		if (!selectedFile.type.startsWith('image/')) {
			setIsUploadingImage(false);
			notify({
				variant: 'error',
				title: translate('spotlight.searchByImage.upload.unsupportedFileType'),
			});

			return;
		}

		try {
			const imageDataURL = await fileToDataUrl(selectedFile);
			const resizedImageDataURL = await resizeImage(imageDataURL);

			if (isReactAssetOverviewActive()) {
				// Already on asset overview page, emit event to trigger search by image with the uploaded image data
				searchForSimilarAssets({
					similarToAssetIds: null,
					similarToImageData: resizedImageDataURL,
					similarToURL: null,
					similarToText: null,
				});
			} else {
				// Search by image upload is done from a page that does not contain the Asset Overview, so we store the image data in the indexedDB and redirect to the asset overview page
				const storedImageDataId = await storeSearchByImageData(resizedImageDataURL);
				const fieldName = SimilarityFieldName.SIMILAR_TO_IMAGE_DATA;
				globalThis.location.href = `/media?${fieldName}=${storedImageDataId}`;
			}
		} catch (err) {
			console.error(err);
			setIsUploadingImage(false);
			notify({
				variant: 'error',
				title: translate('spotlight.searchByImage.upload.error'),
			});
		}
	};

	const searchForSimilarAssets = (similarityFilter: SimilarityFilter) => {
		if (isReactAssetOverviewActive()) {
			emitSimilaritySearchEvent(isHybridSearchOn, similarityFilter);
		} else if (similarityFilter.similarToURL) {
			const searchParams = new URLSearchParams({
				similarToURL: similarityFilter.similarToURL,
			});
			globalThis.location.href = `/media/?${searchParams.toString()}`;
		}

		setIsUploadingImage(false);
		setIsSearchingByLink(false);
		onSubmit();
	};

	const handleSearchingByImageLink = () => {
		setIsSearchingByLink(true);
		searchForSimilarAssets({
			similarToAssetIds: null,
			similarToImageData: null,
			similarToURL: imageURL,
			similarToText: null,
		});
	};

	const handleSearchingByImageLinkOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && !isInvalidInput) {
			handleSearchingByImageLink();
		}
	};

	return (
		<StyledPanel>
			<StyledFocusAnchor ref={focusAnchorRef} tabIndex={-1} />
			<StyledHeader>
				<Button
					ref={returnButtonRef}
					title={translate('generalBack')}
					variant="clean"
					icon={<IconArrowLeft />}
					isPressed={isReturnActive}
					onClick={(event) => {
						event.stopPropagation();
						onClickReturn();
					}}
					isSmall
				/>
				<IconImage />
				<StyledHeaderTitle>{translate('spotlight.searchByImage')}</StyledHeaderTitle>
			</StyledHeader>

			<StyledContent>
				<Flex direction="column" alignItems="center" justifyContent="center" gap="2">
					<Flex justifyContent="space-between" alignItems="center">
						<Paragraph>
							{translate('spotlight.searchByImage.upload.title')}
							<Label>{translate('spotlight.searchByImage.upload.subtitle')}</Label>
						</Paragraph>

						<FileSelector onFilesSelected={handleUpload} contentTypes={['image/*']}>
							<Button
								title="Upload an image"
								variant="primary"
								isLoading={isUploadingImage}
							>
								{translate('spotlight.searchByImage.upload.button')}
							</Button>
						</FileSelector>
					</Flex>

					<CustomDivider />

					<Flex className="search-by-image-link">
						<Input
							value={imageURL}
							onChange={(newValue) => setImageURL(newValue)}
							onKeyDown={handleSearchingByImageLinkOnEnter}
							placeholder={translate('spotlight.searchByImage.link.placeholder')}
							type="url"
							isInvalid={!!(imageURL && isInvalidInput)}
						/>
						<Button
							title="search for an image by url"
							variant="primary"
							onClick={() => handleSearchingByImageLink()}
							isLoading={isSearchingByLink}
							isDisabled={!imageURL || isInvalidInput}
						>
							{translate('generalSearch')}
						</Button>
					</Flex>
					<LinkToKnowledgeBase
						href="https://support.bynder.com/hc/en-us/articles/13812016590354-Search-by-Image#01HFFDP52BJG65YRJJ6JSSKJQP"
						target="_blank"
					>
						{translate('spotlight.searchByImage.link.instructions')}
					</LinkToKnowledgeBase>
				</Flex>
			</StyledContent>

			{showHelpFooter && (
				<StyledFooterWrapper>
					<HelpFooter showSearchKeyboardShortcut />
				</StyledFooterWrapper>
			)}
		</StyledPanel>
	);
}

const StyledPanel = styled.div`
	display: flex;
	flex-direction: column;
`;

const StyledHeader = styled.div`
	display: flex;
	align-items: center;
	gap: ${token.spacing4};
	padding: ${token.spacing3} ${token.spacing5};
`;

const StyledFocusAnchor = styled.span`
	position: absolute;
	width: 0;
	height: 0;
	overflow: hidden;
	outline: none;
`;

const StyledHeaderTitle = styled.span`
	${textStyles.uiTextM}
`;

const StyledContent = styled.div`
	flex: 1;
	padding: 0 ${token.spacing5} ${token.spacing4};

	.search-by-image-link {
		margin: ${token.spacing3} 0;
		gap: ${token.spacing4};
	}
`;

const LinkToKnowledgeBase = styled.a`
	${textStyles.paragraphS};

	color: ${token.gray600a};
	align-self: flex-start;
	margin-top: ${token.spacing2};

	&:hover {
		color: ${token.blue400} !important;
	}
`;

const Paragraph = styled.p`
	${textStyles.paragraphM}
`;

const CustomDivider = styled.div`
	width: 100%;
	height: 1px;
	margin: 8px 0;
	background: #0310261a;
`;

const Label = styled.label`
	color: ${token.gray600a};
	${textStyles.uiTextS}
`;

const StyledFooterWrapper = styled.div`
	position: relative;
	height: ${HELP_FOOTER_HEIGHT}px;
`;
